import { CmpApi } from '@iabtcf/cmpapi'
import type { VendorList } from '@iabtcf/core';
import { TCModel, TCString, GVL } from '@iabtcf/core'
import { AC } from '../ac/AC'
import { Cookie } from '../../helpers'
import { SameSite } from '../../enums/SameSite'
import Logger from '../../helpers/Logger'
import mvFetch from '../../helpers/mvFetch'
import {
  GVL_MINIMUM_VERSION,
  TCF_API_VERSION,
  TCF_CMP_ID,
  TCF_COOKIE_EXPIRES,
  TCF_COOKIE_EXPIRE_MS,
  TCF_DEFAULT_COUNTRY,
  TCF_SERVICE_SPECIFIC_SCOPE,
  TCF_VENDOR_ID_GOOGLE,
  TCF_VENDOR_ID_MEDIAVINE,
  MV_CMP_VERSION
} from '../../constants'
export class TCF {
  public static cmpApi: CmpApi
  public static cookie: Cookie<string> = new Cookie({
    name: 'euconsent-v2',
    sameSite: SameSite.lax,
    maxAge: TCF_COOKIE_EXPIRES
  })
  private static cmpVersionCookie: Cookie<string> = new Cookie({
    name: 'mv_cmp_version',
    sameSite: SameSite.lax,
    maxAge: TCF_COOKIE_EXPIRES
  })
  public static tcModel: TCModel
  public static tcString: string

  /**
   * Initializes the cmpApi, tcModel, and tcString static properties
   */
  public static async init() {
    // We use the cmpiApi middleware to insert Google's addtlConsent string
    TCF.cmpApi = new CmpApi(
      TCF_CMP_ID,
      TCF_API_VERSION,
      TCF_SERVICE_SPECIFIC_SCOPE,
      {
        getTCData: (next, tcData, status) => {
          if (typeof tcData === 'object') {
            tcData.addtlConsent = AC.value
            next(tcData, status)
          } else {
            next(tcData, status)
          }
        }
      }
    )

    // blow out previous cookie/tcModel if not
    // the right cmp version
    if (TCF.cmpVersionCookie.value !== MV_CMP_VERSION) {
      TCF.resetTCFCookies()
    }

    TCF.tcModel = TCF.cookie.value ? TCF.validateTCModel() : new TCModel()
    TCF.tcModel.cmpId = TCF_CMP_ID
    TCF.tcModel.cmpVersion = TCF_API_VERSION
    TCF.tcModel.isServiceSpecific = TCF_SERVICE_SPECIFIC_SCOPE
    TCF.tcModel.publisherCountryCode =
      window.$mediavine.web.model.countryOfOperation || TCF_DEFAULT_COUNTRY
    TCF.tcModel.supportOOB = false

    try {
      TCF.tcModel.customPurposes = await TCF.fetchCustomPurposes()
      TCF.tcModel.gvl = await TCF.fetchGvl()
    } catch (error) {
      const errorMessage = 'GDPR: Error fetch TC Model Data'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    }

    TCF.tcString = TCString.encode(TCF.tcModel)

    window.dispatchEvent(
      new CustomEvent('mediavineCmp', {
        detail: 'tcfApiReady'
      })
    )
  }

  /** Encodes an updated tcString when applicable; informs the cmpApi about the UI status*/
  public static consentDoesApply({ displayUi }: { displayUi: boolean }): void {
    if (displayUi) {
      // inform cmpApi we're opening the UI but we don't have consent yet
      TCF.cmpApi.update('', displayUi)
    } else {
      // inform cmpApi we're closing the UI and we now have (or already had) consent
      TCF.tcString = TCString.encode(TCF.tcModel)
      TCF.cmpApi.update(TCF.tcString, displayUi)
      window.$mediavine.newrelic.addPageAction(
        'gdprModal',
        TCF.getVendorConsents([TCF_VENDOR_ID_GOOGLE, TCF_VENDOR_ID_MEDIAVINE])
      )
    }
  }

  /** 
   * Sets the 'euconsent-v2' cookie with the TCF.tcString 
   * and 'mv_cmp_version' value
   */
  public static setCookie(): void {
    TCF.cookie.set(TCF.tcString)
    TCF.cmpVersionCookie.set(MV_CMP_VERSION)
  }

  /** Returns an array of valid purposeConsents for specific purpose IDs */
  public static getPurposeConsents(purposeIds: Array<number>): Array<number> {
    return purposeIds.filter((id) => TCF.tcModel.purposeConsents.has(id))
  }

  /** Returns an array of valid vendorConsents for specific vendor IDs */
  public static getVendorConsents(vendorIds: Array<number>): Array<number> {
    return vendorIds.filter((id) => TCF.tcModel.vendorConsents.has(id))
  }

  /** Returns an array of valid vendorLegitimateInterest for specific vendor IDs */
  public static getVendorLegitimateInterests(
    vendorIds: Array<number>
  ): Array<number> {
    return vendorIds.filter((id) => {
      const supportsLegitInt =
        TCF.tcModel.gvl.vendors[id].legIntPurposes.length > 0
      return supportsLegitInt && TCF.tcModel.vendorLegitimateInterests.has(id)
    })
  }

  /** Fetches customPurposes from the WebModel or the '/cmp/<slug>/purposes' endpoint  */
  private static async fetchCustomPurposes(): Promise<any | undefined> {
    const { launcherUrl, slug } = window.$mediavine.web.model

    if (!window.$mediavine.web.model.customPurposes) {
      window.$mediavine.web.model.customPurposes = await mvFetch.fetchJson(
        `${launcherUrl}/cmp/${slug}/purposes.json`,
        'GET'
      )
    }

    const { customPurposes } = window.$mediavine.web.model

    if (customPurposes && customPurposes.length > 0) {
      const customPurposesMap = {}
      // reverse the array to format it nicely for the CMP
      customPurposes.reverse()
      customPurposes.forEach((customPurpose) => {
        customPurposesMap[customPurpose.id] = {
          name: customPurpose.name,
          description: customPurpose.description
        }
      })
      return customPurposesMap
    }
  }

  /** Fetches the GVL from the WebModel or the '/cmp/vendor-list.json' endpoint  */
  private static async fetchGvl(): Promise<GVL> {
    const { launcherUrl } = window.$mediavine.web.model
    let fetchedGvl: object | undefined = window.$mediavine.web.model.gvl
    if (!fetchedGvl) {
      fetchedGvl = await mvFetch.fetchJson(
        `${launcherUrl}/cmp/vendor-list.json`,
        'GET'
      )
    }
    return new GVL(fetchedGvl as VendorList)
  }

  private static validateTCModel(): TCModel {
    const tcModel = TCString.decode(TCF.cookie.value)
    const { vendorListVersion, created } = tcModel
    if (
      /**
       * User with an existing consent string will need to reconsent
       * based upon their vendorListVersion
       * */
      GVL_MINIMUM_VERSION > vendorListVersion ||
      !TCF.isTCAgeValid(created)
    ) {
      // The user needs to reconsent. Clear existing consent and create a new TCModel
      TCF.resetTCFCookies()
      return new TCModel()
    } else {
      return tcModel
    }
  }

  /**
   * clears out tcf previous settings/data, should
   * make user reconsent
   */
  private static resetTCFCookies () {
    AC.setSome([])
    TCF.cookie.set('')
  }

  /**
   * check created age to protect against edge cases where cookie
   * maxAge could be "started over later" (?test=gdpr), maybe others
   * */
  private static isTCAgeValid(created?: Date): boolean {
    if (!created) {
      return false
    }
    const expiryUnit = (Date.now() - created.valueOf()) / TCF_COOKIE_EXPIRE_MS

    return expiryUnit < 1
  }
}

export interface IVendorConsents {
  consents: Array<number>
  legitInts: Array<number>
}
